import React from "react"
import { ChevronDown, User, HelpCircle, LogOut } from "../components/ui/Icon"
import NavItem from "../components/Commons/NavItem"
import { ThemeToggle } from "../components/ui"
import { clearSelections } from "../redux/actions/dashboardListActions"
import { resetDynamicTableFetchGuard } from "../redux/actions/dynamicTableActions"
import { getRolePermissions } from "../utils/roles"
import { jwtDecode } from "jwt-decode"
import { setPermissions } from "../redux/actions/rollActions"
import constants from "./en"
import { getDashboardReportsList } from "../redux/actions/dashboardActions"
import {
  setToken,
  setUser,
  loginFailure,
  userLogAuditing,
} from "../redux/actions/authActions"
import { AuthTokenManager } from "./commonUtills"
import {
  setNewRelicUserContext,
  clearNewRelicUserContext,
} from "./telemetry"
import { toHomePath } from "./routePaths"
import AppDropdownMenu from "../components/ui/DropdownMenu/AppDropdownMenu"
import { USER_TYPES } from "./enums"
import { getCustomISODate } from "../utils"

// ── Nav items data config (replaces renderAdminMenu / renderFacilityAdminMenu / renderFacilityUserMenu) ──
export const NAV_ITEMS = {
  [USER_TYPES.SUPER_ADMIN]: [
    { to: "overview", label: constants.HOME, activeKeys: ["overview"] },
    { to: "dashboard", label: constants.DASHBOARD, activeKeys: ["dashboard"] },
    {
      to: "schedule-management",
      label: constants.SCHEDULING,
      activeKeys: [
        "schedule-management",
        "scheduling-confirmations",
        "master-calendar",
      ],
      children: [
        {
          to: "schedule-management/scheduling-confirmations",
          label: "Confirmations",
        },
        { to: "schedule-management/master-calendar", label: "Master Calendar" },
      ],
    },
    {
      to: "spend-management",
      label: constants.SPEND_MANAGEMENT,
      activeKeys: ["spend-management", "spend-mtg-invoices"],
      children: [
        { to: "spend-management", label: "Timesheets" },
        { to: "spend-management/spend-mtg-invoices", label: "Invoices" },
      ],
    },
  ],
  [USER_TYPES.FACILITY_ADMIN]: [
    {
      to: "my-facility",
      label: constants.HOME,
      activeKeys: ["", "my-facility"],
    },
    { to: "dashboard", label: constants.DASHBOARD, activeKeys: ["dashboard"] },
    {
      to: "schedule-management",
      label: constants.SCHEDULING,
      activeKeys: [
        "schedule-management",
        "scheduling-confirmations",
        "master-calendar",
      ],
      children: [
        {
          to: "schedule-management/scheduling-confirmations",
          label: "Confirmations",
        },
        { to: "schedule-management/master-calendar", label: "Master Calendar" },
      ],
    },
    {
      to: "spend-management",
      label: constants.SPEND_MANAGEMENT,
      activeKeys: ["spend-management", "spend-mtg-invoices"],
      children: [
        { to: "spend-management", label: "Timesheets" },
        { to: "spend-management/spend-mtg-invoices", label: "Invoices" },
      ],
    },
  ],
  [USER_TYPES.FACILITY_USER]: [
    {
      key: "home",
      to: "dashboard/landing-page",
      label: constants.HOME,
      activeKeys: ["", "dashboard/landing-page"],
    },
    { to: "dashboard", label: constants.DASHBOARD, activeKeys: ["dashboard"] },
    {
      to: "schedule-management",
      label: constants.SCHEDULING,
      activeKeys: [
        "schedule-management",
        "scheduling-confirmations",
        "master-calendar",
      ],
      permission: "schedule_management",
      children: [
        {
          to: "schedule-management/scheduling-confirmations",
          label: "Confirmations",
        },
        { to: "schedule-management/master-calendar", label: "Master Calendar" },
      ],
    },
    {
      to: "spend-management",
      label: constants.SPEND_MANAGEMENT,
      activeKeys: ["spend-management", "spend-mtg-invoices"],
      permission: "spend_management_menu",
      children: [
        { to: "spend-management", label: "Timesheets" },
        { to: "spend-management/spend-mtg-invoices", label: "Invoices" },
      ],
    },
  ],
}

/**
 * Returns filtered nav items for the given role + permissions.
 * Replaces renderAdminMenu / renderFacilityAdminMenu / renderFacilityUserMenu.
 */
export const getNavItems = (rolePermission) => {
  const role = rolePermission?.role
  const items = NAV_ITEMS[role] || []
  const permissions = rolePermission?.permissions

  return items.filter((item) => {
    if (!item.permission) return true
    return Boolean(permissions?.[item.permission])
  })
}

// Clears the global client/facility selection filters. Dispatched only on
// initial Header mount — NOT on every activeMenu change — so that moving
// between Scheduling and Spend (which share the selected client) preserves
// the user's selection after back-navigation.
export const resetSelectionsOnMount = (dispatch) => {
  dispatch(clearSelections())
}

// Refreshes dashboard reports when the main nav section changes.
export const initializeData = (dispatch) => {
  dispatch(getDashboardReportsList())
}

export const handleLogoClick = (navigate) => {
  navigate(toHomePath(""))
  localStorage.setItem("activeMenu", "")
}

// Auth0 errors requiring interactive re-login (stale cache, expired refresh token).
const UNRECOVERABLE_AUTH_ERRORS =
  /missing\s*refresh\s*token|login_required|consent_required|invalid_grant/i

export const isUnrecoverableAuthError = (error) => {
  if (!error) return false
  const message = error.message || error.error_description || String(error)
  return UNRECOVERABLE_AUTH_ERRORS.test(message)
}

export const fetchAuthToken = async (
  dispatch,
  user,
  getAccessTokenSilently,
  onAuthFailure,
) => {
  try {
    const token = await getAccessTokenSilently({
      audience: process.env.REACT_APP_Auth0Audience,
      scope: process.env.REACT_APP_Auth0Scope,
      detailedResponse: true,
      useRefreshTokens: true,
    })
    const { access_token } = token
    const decodedToken = jwtDecode(access_token)
    AuthTokenManager.set(access_token)
    const tokenData = decodedToken || {}
    const roleAndPermission = getRolePermissions(tokenData)
    localStorage.setItem(
      "sessionTime",
      JSON.stringify({ startTime: Date.now() }),
    )
    dispatch(setPermissions(roleAndPermission))
    dispatch(setToken(token))
    dispatch(setUser(user))

    // Attribute all subsequent New Relic Browser events to this user so the
    // usage dashboard can facet frontend activity by user, role and facility.
    setNewRelicUserContext({
      userId: tokenData.user_id,
      role: roleAndPermission.role,
      facilityId:
        tokenData.facility_account_no ??
        tokenData["custom-data"]?.[0]?.facility_account_no ??
        tokenData.client_account_no ??
        null,
    })

    // Login fully succeeded — emit the audit once per browser session.
    // Skipped when the token has no user_id or no usable role (token refreshes
    // also skip because the sessionStorage flag is already set).
    const auditKey = "loginAuditFired"
    if (
      tokenData.user_id != null &&
      roleAndPermission.role &&
      !sessionStorage.getItem(auditKey)
    ) {
      sessionStorage.setItem(auditKey, "1")
      await userLogAuditing([
        {
          user_id: tokenData.user_id,
          last_login: getCustomISODate(),
          mfa_enrollment: true,
        },
      ])
    }
  } catch (error) {
    dispatch(loginFailure(error || "Login failed"))
    console.error("Error fetching access token:", error)
    if (
      typeof onAuthFailure === "function" &&
      isUnrecoverableAuthError(error)
    ) {
      onAuthFailure(error)
    }
  }
}
export const handleReset = (logout) => {
  logout({ returnTo: window.location.origin })
}

export const handleLogout = (logout, navigate, dispatch) => {
  clearNewRelicUserContext()
  localStorage.clear()
  sessionStorage.clear()
  resetDynamicTableFetchGuard()
  logout({ returnTo: window.location.origin })
}

const getInitials = (fullName) => {
  if (!fullName) return "?"
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export const renderProfileSection = (
  isMobile,
  userList,
  userName,
  handleLogout,
  navigate,
) => {
  const profileItems = [
    ...(userName ? [{ type: "header", label: userName }] : []),
    {
      label: constants.MY_PROFILE,
      icon: <User size={14} />,
      onClick: () => navigate(toHomePath("profile")),
    },
    {
      label: constants.SUPPORT_HELP,
      icon: <HelpCircle size={14} />,
      onClick: () => navigate(toHomePath("knowledge")),
    },
    { separator: true },
    {
      label: constants.LOGOUT,
      icon: <LogOut size={14} />,
      onClick: handleLogout,
      danger: true,
    },
  ]

  return (
    <div className="profile-section">
      <ThemeToggle />
      <AppDropdownMenu
        trigger={
          <button className="avatar-btn" aria-label="User menu">
            <span className="avatar-circle">{getInitials(userName)}</span>
            <ChevronDown size={16} className="avatar-caret" />
          </button>
        }
        align="end"
        items={profileItems}
      />
    </div>
  )
}

export const renderAdminMenu = (activeMenu) => (
  <div className="topnav-links" id="navbarNav">
    <ul className="topnav-list">
      <NavItem to="" label={constants.FACILITY} activeMenu={activeMenu} />
      <NavItem
        to="dashboard"
        label={constants.DASHBOARD}
        activeMenu={activeMenu}
      />
      <NavItem
        to="schedule-management"
        label={constants.SCHEDULING}
        activeMenu={activeMenu}
        activeKeys={[
          "schedule-management",
          "scheduling-confirmations",
          "master-calendar",
        ]}
      >
        {[
          {
            to: "schedule-management/scheduling-confirmations",
            label: "Confirmations",
            activeKey: "scheduling-confirmations",
          },
          {
            to: "schedule-management/master-calendar",
            label: "Master Calendar",
            activeKey: "master-calendar",
          },
        ]}
      </NavItem>
      <NavItem
        to="spend-management"
        label={constants.SPEND_MANAGEMENT}
        activeMenu={activeMenu}
        activeKeys={["spend-management", "spend-mtg-invoices"]}
      >
        {[
          {
            to: "spend-management",
            label: "Timesheets",
            activeKey: "spend-management",
          },
          {
            to: "spend-management/spend-mtg-invoices",
            label: "Invoices",
            activeKey: "spend-mtg-invoices",
          },
        ]}
      </NavItem>
    </ul>
  </div>
)

export const renderFacilityAdminMenu = (rolePermission, activeMenu) => (
  <div className="topnav-links" id="navbarNav">
    <ul className="topnav-list">
      <NavItem
        to="my-facility"
        label={constants.HOME}
        activeMenu={activeMenu}
        activeKeys={["", "my-facility"]}
      />
      <NavItem
        to="dashboard"
        label={constants.DASHBOARD}
        activeMenu={activeMenu}
      />
      <NavItem
        to="schedule-management"
        label={constants.SCHEDULING}
        activeMenu={activeMenu}
        activeKeys={[
          "schedule-management",
          "scheduling-confirmations",
          "master-calendar",
        ]}
      >
        {[
          {
            to: "schedule-management/scheduling-confirmations",
            label: "Confirmations",
            activeKey: "scheduling-confirmations",
          },
          {
            to: "schedule-management/master-calendar",
            label: "Master Calendar",
            activeKey: "master-calendar",
          },
        ]}
      </NavItem>
      <NavItem
        to="spend-management"
        label={constants.SPEND_MANAGEMENT}
        activeMenu={activeMenu}
        activeKeys={["spend-management", "spend-mtg-invoices"]}
      >
        {[
          {
            to: "spend-management",
            label: "Timesheets",
            activeKey: "spend-management",
          },
          {
            to: "spend-management/spend-mtg-invoices",
            label: "Invoices",
            activeKey: "spend-mtg-invoices",
          },
        ]}
      </NavItem>
    </ul>
  </div>
)

export const renderFacilityUserMenu = (rolePermission, activeMenu) => (
  <div className="topnav-links" id="navbarNav">
    <ul className="topnav-list">
      <NavItem
        to="dashboard/landing-page"
        label={constants.HOME}
        activeMenu={activeMenu}
        activeKeys={["", "dashboard/landing-page"]}
      />
      <NavItem
        to="dashboard"
        label={constants.DASHBOARD}
        activeMenu={activeMenu}
      />
      {rolePermission?.permissions?.schedule_management && (
        <NavItem
          to="schedule-management"
          label={constants.SCHEDULING}
          activeMenu={activeMenu}
          activeKeys={[
            "schedule-management",
            "scheduling-confirmations",
            "master-calendar",
          ]}
        >
          {[
            {
              to: "schedule-management/scheduling-confirmations",
              label: "Confirmations",
              activeKey: "scheduling-confirmations",
            },
            {
              to: "schedule-management/master-calendar",
              label: "Master Calendar",
              activeKey: "master-calendar",
            },
          ]}
        </NavItem>
      )}
      {rolePermission?.permissions?.spend_management_menu && (
        <NavItem
          to="spend-management"
          label={constants.SPEND_MANAGEMENT}
          activeMenu={activeMenu}
          activeKeys={["spend-management", "spend-mtg-invoices"]}
        >
          {[
            {
              to: "spend-management",
              label: "Timesheets",
              activeKey: "spend-management",
            },
            {
              to: "spend-management/spend-mtg-invoices",
              label: "Invoices",
              activeKey: "spend-mtg-invoices",
            },
          ]}
        </NavItem>
      )}
    </ul>
  </div>
)
